import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  Search, Filter, FileText, Clock,
  User, Wrench, Eye, ArrowLeft,
  CheckCircle, XCircle, Circle, Archive
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'

import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Select from '../components/ui/Select'
import Modal from '../components/Modal'
import Pagination from '../components/ui/Pagination'

import ordensServicoService, {
  OrdemServicoFilters,
  getPrioridadeConfig,
  formatValor,
  prioridades
} from '../services/ordensServico'
import { OrdemServico, EstadoOS } from '../types'
import OrdemServicoDetails from '../components/ordensServico/OrdemServicoDetails'

const OrdensServicoArquivoPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  // Estados locais
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedOS, setSelectedOS] = useState<OrdemServico | null>(null)
  const [showFilters, setShowFilters] = useState(false)

  // Filtros - sempre com arquivada=true
  const [filters, setFilters] = useState<OrdemServicoFilters>({
    page: parseInt(searchParams.get('page') || '1'),
    limit: parseInt(searchParams.get('limit') || '25'),
    search: searchParams.get('search') || '',
    estadoId: searchParams.get('estadoId') ? parseInt(searchParams.get('estadoId')!) : undefined,
    prioridade: (searchParams.get('prioridade') as any) || undefined,
    usuarioResponsavelId: searchParams.get('usuarioResponsavelId') ? parseInt(searchParams.get('usuarioResponsavelId')!) : undefined,
    arquivada: true // Sempre mostrar apenas arquivadas
  })

  // Queries
  const { data: ordensData, isLoading, refetch } = useQuery({
    queryKey: ['ordens-servico-arquivo', filters],
    queryFn: () => ordensServicoService.list(filters)
  })

  const { data: estados } = useQuery({
    queryKey: ['estados-os'],
    queryFn: () => ordensServicoService.getEstados()
  })

  // Efeitos
  useEffect(() => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '' && value !== null && key !== 'arquivada') {
        params.set(key, String(value))
      }
    })
    setSearchParams(params)
  }, [filters, setSearchParams])

  // Handlers
  const handleSearch = (search: string) => {
    setFilters(prev => ({ ...prev, search, page: 1 }))
  }

  const handleFilterChange = (key: keyof OrdemServicoFilters, value: any) => {
    if (key === 'arquivada') return // Não permitir alterar o filtro arquivada
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }))
  }

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }))
  }

  const handleView = (os: OrdemServico) => {
    setSelectedOS(os)
    setShowDetailsModal(true)
  }

  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: 25,
      search: '',
      estadoId: undefined,
      prioridade: undefined,
      usuarioResponsavelId: undefined,
      arquivada: true // Manter sempre como arquivada
    })
  }

  // Ícone do estado
  const getStateIcon = (estado: EstadoOS) => {
    const name = estado.nome.toLowerCase()
    if (name.includes('conclu') || name.includes('entregue')) {
      return <CheckCircle className="h-4 w-4 text-green-500" />
    }
    if (name.includes('cancel') || name.includes('recus')) {
      return <XCircle className="h-4 w-4 text-red-500" />
    }
    if (name.includes('anda') || name.includes('repar')) {
      return <Wrench className="h-4 w-4 text-blue-500" />
    }
    if (name.includes('espera') || name.includes('pend')) {
      return <Clock className="h-4 w-4 text-yellow-500" />
    }
    return <Circle className="h-4 w-4 text-gray-500" />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/ordens-servico')}
              leftIcon={<ArrowLeft className="h-4 w-4" />}
            >
              Voltar
            </Button>
            <Archive className="h-6 w-6 text-gray-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Arquivo de Ordens de Serviço</h1>
          <p className="text-gray-600">
            {ordensData?.pagination?.totalItems || 0} ordens de serviço arquivadas (fechadas/canceladas)
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            leftIcon={<Filter className="h-4 w-4" />}
          >
            Filtros
          </Button>
        </div>
      </div>

      {/* Barra de pesquisa */}
      <div className="flex gap-4">
        <div className="flex-1">
          <Input
            placeholder="Buscar por número, cliente, equipamento..."
            value={filters.search}
            onChange={(e) => handleSearch(e.target.value)}
            leftIcon={<Search className="h-4 w-4" />}
          />
        </div>
      </div>

      {/* Filtros avançados */}
      {showFilters && (
        <div className="bg-gray-50 p-4 rounded-lg grid grid-cols-1 md:grid-cols-4 gap-4">
          <Select
            label="Estado"
            value={filters.estadoId || ''}
            onChange={(e) => handleFilterChange('estadoId', e.target.value ? parseInt(e.target.value) : undefined)}
            options={[
              { value: '', label: 'Todos os estados' },
              ...(estados?.filter((estado: any) => {
                const nome = estado.nome.toLowerCase()
                return estado.estadoFinal ||
                       nome.includes('entregue') ||
                       nome.includes('cancelad') ||
                       nome.includes('fechad') ||
                       nome.includes('concluíd') ||
                       nome.includes('finaliz')
              }).map((estado: any) => ({
                value: estado.id.toString(),
                label: estado.nome
              })) || [])
            ]}
          />

          <Select
            label="Prioridade"
            value={filters.prioridade || ''}
            onChange={(value) => handleFilterChange('prioridade', value || undefined)}
            options={[
              { value: '', label: 'Todas as prioridades' },
              ...prioridades.map(prioridade => ({
                value: prioridade.value,
                label: prioridade.label
              }))
            ]}
          />

          <div className="md:col-span-2 flex justify-end">
            <Button
              variant="outline"
              onClick={clearFilters}
            >
              Limpar Filtros
            </Button>
          </div>
        </div>
      )}

      {/* Lista de ordens de serviço */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Carregando ordens de serviço arquivadas...</p>
          </div>
        ) : ordensData?.ordensServico?.length === 0 ? (
          <div className="p-8 text-center">
            <Archive className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">Nenhuma ordem de serviço arquivada encontrada</h3>
            <p className="text-gray-600">
              {Object.keys(filters).some(key => filters[key as keyof OrdemServicoFilters] && key !== 'page' && key !== 'limit' && key !== 'arquivada')
                ? 'Tente ajustar os filtros.'
                : 'Quando ordens de serviço forem fechadas ou canceladas, aparecerão aqui.'
              }
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ordem / Cliente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Equipamento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado Final
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Prioridade
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Técnico
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {ordensData?.ordensServico?.map((os) => {
                    const prioridadeConfig = getPrioridadeConfig(os.prioridade)

                    return (
                      <tr key={os.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {os.numero}
                            </div>
                            <div className="text-sm text-gray-500">
                              {os.cliente.nome}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {os.equipamento.modelo.marca.nome} {os.equipamento.modelo.nome}
                          </div>
                          <div className="text-sm text-gray-500">
                            {os.equipamento.modelo.tipoEquipamento.nome}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {getStateIcon(os.estado)}
                            <span className="text-sm text-gray-900">
                              {os.estado.nome}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 text-sm font-medium rounded-full ${
                            prioridadeConfig.color === 'green' ? 'bg-green-100 text-green-800' :
                            prioridadeConfig.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                            prioridadeConfig.color === 'red' ? 'bg-red-100 text-red-800' :
                            prioridadeConfig.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {prioridadeConfig.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-900">
                              {os.usuarioResponsavel?.nome || 'Não atribuído'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {formatValor(os.valorTotalComIva)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleView(os)}
                              title="Ver detalhes"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {ordensData && ordensData.pagination && (
              <Pagination
                pagination={ordensData.pagination}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </div>

      {/* Modal de detalhes */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title="Detalhes da Ordem de Serviço Arquivada"
        size="xl"
      >
        {selectedOS && (
          <OrdemServicoDetails
            ordemServico={selectedOS}
            onEdit={() => {}} // Não permitir edição em OS arquivadas
            onClose={() => setShowDetailsModal(false)}
            readOnly={true} // Modo somente leitura
          />
        )}
      </Modal>
    </div>
  )
}

export default OrdensServicoArquivoPage